/**
 * Ecash proof data structures for Cashero.
 *
 * A Proof is an unblinded signature that represents a claim on a specific amount
 * of piconero held by the mint. A collection of proofs forms a spendable token.
 */

import type { UnblindedSignature } from './blind-signature';

/**
 * A single ecash proof — the atomic unit of value in Cashero.
 * This is what a wallet holds and what gets verified/consumed by the mint.
 */
export interface Proof {
  /** The secret that was blinded (64-char hex string) */
  secret: string;
  /** The unblinded signature point (hex-encoded curve point) */
  C: string;
  /** The keyset ID this proof belongs to */
  id: string;
  /** The amount this proof represents (in piconero) */
  amount: number;
}

/**
 * Create a Proof from an UnblindedSignature returned by the mint.
 */
export function proofFromUnblinded(sig: UnblindedSignature): Proof {
  return {
    secret: sig.secret,
    C: sig.C,
    id: sig.id,
    amount: sig.amount,
  };
}

/**
 * Compute the SHA-256 hash of a proof's secret (for spent-secret tracking).
 */
import { sha256 } from '@noble/hashes/sha2';
import { bytesToHex } from '@noble/hashes/utils';
import { utf8ToBytes } from '@noble/hashes/utils';

export function hashSecret(secret: string): string {
  return bytesToHex(sha256(utf8ToBytes(secret)));
}

/**
 * Validate that a proof has all required fields and valid structure.
 * Does NOT verify cryptographic integrity (use verifyProof in blind-signature.ts for that).
 */
export function validateProof(proof: Proof): boolean {
  if (!proof.secret || typeof proof.secret !== 'string') return false;
  if (proof.secret.length !== 64 || !/^[0-9a-f]{64}$/i.test(proof.secret)) return false;
  if (!proof.C || typeof proof.C !== 'string') return false;
  if (!proof.id || typeof proof.id !== 'string') return false;
  if (typeof proof.amount !== 'number' || proof.amount <= 0) return false;
  // Verify C is a valid hex string at minimum
  if (!/^[0-9a-f]+$/i.test(proof.C)) return false;
  return true;
}

/**
 * Sum the total amount of an array of proofs (in piconero).
 */
export function sumProofs(proofs: Proof[]): number {
  return proofs.reduce((sum, p) => sum + p.amount, 0);
}

/**
 * Group proofs by mint (by keyset ID prefix).
 */
export function groupProofsByKeyset(proofs: Proof[]): Record<string, Proof[]> {
  const groups: Record<string, Proof[]> = {};
  for (const proof of proofs) {
    if (!groups[proof.id]) groups[proof.id] = [];
    groups[proof.id].push(proof);
  }
  return groups;
}
